import * as React from 'react'
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    InputAdornment,
    Slider,
    Switch,
    TextField,
    Typography
} from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import useAutoTranslate from '../hooks/useAutoTranslate'
import {
    ATTACK_TEMPLATE_FIELDS,
    getAttackSetupAssignment,
    getSavedAttackTemplateReference,
    syncAttackTemplateSelection
} from './attackTemplateAssignments'
import { buildSavedAttackTemplatesUrl } from './attackTemplatePlatform'
import CompactAssetIcon from './CompactAssetIcon'
import { KHAN_DEFENSE_TOOL_ICON_BY_KEY } from './gameAssetIcons'
import UnitIconFrame from './UnitIconFrame'

export const KHAN_CONFIG_KEY = 'khanAttackConfig'

const SKIP_OPTIONS = [
    { value: 1, label: '1 min' },
    { value: 5, label: '5 min' },
    { value: 10, label: '10 min' },
    { value: 30, label: '30 min' },
    { value: 60, label: '1 hour' },
    { value: 300, label: '5 hours' },
    { value: 1440, label: '24 hours' }
]

const PACKAGE_TIERS = [
    { key: 'tier1', label: 'Tier 1', medals: 95 },
    { key: 'tier2', label: 'Tier 2', medals: 475 },
    { key: 'tier3', label: 'Tier 3', medals: 1425 }
]

const DEFENSE_TOOL_PURCHASES = [
    ['buyHurlingRocks', 'Buy Hurling Rocks', 'hurlingRocksMinimum', 'Hurling Rocks Minimum', 'Auto-buy Hurling Rocks when below minimum'],
    ['buyCastleGateReinforcement', 'Buy Castle Gate Reinforcement', 'castleGateReinforcementMinimum', 'Castle Gate Reinforcement Minimum', 'Auto-buy Castle Gate Reinforcement when below minimum'],
    ['buyFlamingArrows', 'Buy Flaming Arrows', 'flamingArrowsMinimum', 'Flaming Arrows Minimum', 'Auto-buy Flaming Arrows when below minimum'],
    ['buySharpenedStakes', 'Buy Sharpened Stakes', 'sharpenedStakesMinimum', 'Sharpened Stakes Minimum', 'Auto-buy Sharpened Stakes when below minimum'],
    ['buyBodkinArrowheads', 'Buy Bodkin Arrowheads', 'bodkinArrowheadsMinimum', 'Bodkin Arrowheads Minimum', 'Auto-buy Bodkin Arrowheads when below minimum']
]

const PRIMARY_CARD_SX = {
    backgroundColor: 'rgba(18, 29, 48, 0.82)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '22px',
    boxShadow: '0 16px 34px rgba(0,0,0,0.18)'
}

const NESTED_CARD_SX = {
    backgroundColor: 'rgba(15, 27, 46, 0.82)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '16px'
}

const FIELD_INPUT_SX = {
    '& .MuiOutlinedInput-root': {
        minHeight: 44,
        borderRadius: '10px',
        backgroundColor: 'rgba(23, 34, 55, 0.72)'
    },
    '& .MuiInputBase-input': {
        fontSize: '1.01rem',
        fontWeight: 700,
        color: '#f5f7ff'
    }
}

const TOGGLE_SWITCH_SX = {
    width: 48,
    height: 29,
    p: 0,
    '& .MuiSwitch-switchBase': {
        p: '2.5px',
        '&.Mui-checked': {
            transform: 'translateX(18px)',
            color: '#ffffff'
        },
        '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: '#2f7cff',
            opacity: 1
        }
    },
    '& .MuiSwitch-thumb': {
        width: 24,
        height: 24,
        boxShadow: 'none'
    },
    '& .MuiSwitch-track': {
        opacity: 1,
        borderRadius: '999px',
        backgroundColor: 'rgba(111, 123, 143, 0.9)'
    }
}

const OUTLINED_ACTION_SX = {
    minHeight: 36,
    px: 1.35,
    borderRadius: '10px',
    borderColor: 'rgba(112, 136, 174, 0.34)',
    backgroundColor: 'rgba(42, 54, 78, 0.56)',
    color: '#eef4ff',
    textTransform: 'none',
    fontWeight: 700,
    '&:hover': {
        borderColor: 'rgba(112, 136, 174, 0.5)',
        backgroundColor: 'rgba(50, 63, 88, 0.7)'
    }
}

const clampPercent = value => {
    const nextValue = Math.round(Number(value))
    if (!Number.isFinite(nextValue))
        return 0

    return Math.min(100, Math.max(0, nextValue))
}

const cloneValue = value => {
    if (typeof structuredClone === 'function')
        return structuredClone(value)

    return JSON.parse(JSON.stringify(value))
}

const sortTemplatesByUpdatedAt = templates =>
    [...(Array.isArray(templates) ? templates : [])].sort((left, right) =>
        Number(right?.updatedAt ?? 0) - Number(left?.updatedAt ?? 0) ||
        String(left?.name ?? '').localeCompare(String(right?.name ?? ''))
    )

const createDefaultConfig = () => ({
    attackCountThreshold: '4200',
    maxKhanAttacks: '100',
    maxChainLength: '0',
    refillToolsAfterHits: '45',
    allowedSkipTypes: [10, 30, 60, 300, 1440],
    useKhanChests: false,
    courtyardMeleePercentage: '50',
    moatLeftTool: '',
    moatMiddleTool: '',
    moatRightTool: '',
    wallLeftTool: '',
    wallMiddleTool: '',
    wallRightTool: '',
    wallMiddleGateTool: '',
    wallMeleePercentage: '5',
    wallTroopSplit: { left: 38, middle: 24, right: 38 },
    buyIntervalSeconds: '60',
    buyMeleeDefense: false,
    minimumMeleeDefense: '1000',
    buyRangeDefense: false,
    minimumRangeDefense: '1000',
    troopPackageTiers: {
        tier1: { melee: false, ranged: false },
        tier2: { melee: false, ranged: false },
        tier3: { melee: false, ranged: false }
    },
    medalOverflowProtection: false,
    minimumCoins: '10000000',
    minimumKhanTablets: '50000',
    buyHurlingRocks: false,
    hurlingRocksMinimum: '15000',
    buyCastleGateReinforcement: false,
    castleGateReinforcementMinimum: '15000',
    buyFlamingArrows: false,
    flamingArrowsMinimum: '15000',
    buySharpenedStakes: false,
    sharpenedStakesMinimum: '15000',
    buyBodkinArrowheads: false,
    bodkinArrowheadsMinimum: '15000',
    keepTool: '',
    keepSupportTool: '',
    eventDifficulty: '3',
    nomadsScoreShutoff: '0',
    hanTauntTargetCount: '0'
})

const normalizePackageTiers = value => {
    const base = createDefaultConfig().troopPackageTiers
    const source = value && typeof value === 'object' ? value : {}

    return {
        tier1: { melee: Boolean(source?.tier1?.melee ?? base.tier1.melee), ranged: Boolean(source?.tier1?.ranged ?? base.tier1.ranged) },
        tier2: { melee: Boolean(source?.tier2?.melee ?? base.tier2.melee), ranged: Boolean(source?.tier2?.ranged ?? base.tier2.ranged) },
        tier3: { melee: Boolean(source?.tier3?.melee ?? base.tier3.melee), ranged: Boolean(source?.tier3?.ranged ?? base.tier3.ranged) }
    }
}

const normalizeWallTroopSplit = value => {
    const fallback = createDefaultConfig().wallTroopSplit
    const source = value && typeof value === 'object' ? value : fallback
    const left = clampPercent(source.left ?? fallback.left)
    const middle = clampPercent(source.middle ?? fallback.middle)
    const right = clampPercent(source.right ?? fallback.right)
    const total = left + middle + right

    if (total === 100)
        return { left, middle, right }
    if (total <= 0)
        return fallback

    const nextLeft = Math.round((left / total) * 100)
    const nextMiddle = Math.round((middle / total) * 100)
    return { left: nextLeft, middle: nextMiddle, right: Math.max(0, 100 - nextLeft - nextMiddle) }
}

const normalizeConfig = value => {
    const base = createDefaultConfig()
    const source = value && typeof value === 'object' ? value : {}

    return {
        attackCountThreshold: String(source.attackCountThreshold ?? base.attackCountThreshold),
        maxKhanAttacks: String(source.maxKhanAttacks ?? base.maxKhanAttacks),
        maxChainLength: String(source.maxChainLength ?? base.maxChainLength),
        refillToolsAfterHits: String(source.refillToolsAfterHits ?? base.refillToolsAfterHits),
        allowedSkipTypes: Array.isArray(source.allowedSkipTypes) ? source.allowedSkipTypes.map(Number).filter(Number.isFinite) : [...base.allowedSkipTypes],
        useKhanChests: Boolean(source.useKhanChests ?? base.useKhanChests),
        courtyardMeleePercentage: String(source.courtyardMeleePercentage ?? base.courtyardMeleePercentage),
        moatLeftTool: String(source.moatLeftTool ?? base.moatLeftTool),
        moatMiddleTool: String(source.moatMiddleTool ?? base.moatMiddleTool),
        moatRightTool: String(source.moatRightTool ?? base.moatRightTool),
        wallLeftTool: String(source.wallLeftTool ?? base.wallLeftTool),
        wallMiddleTool: String(source.wallMiddleTool ?? base.wallMiddleTool),
        wallRightTool: String(source.wallRightTool ?? base.wallRightTool),
        wallMiddleGateTool: String(source.wallMiddleGateTool ?? base.wallMiddleGateTool),
        wallMeleePercentage: String(source.wallMeleePercentage ?? base.wallMeleePercentage),
        wallTroopSplit: normalizeWallTroopSplit(source.wallTroopSplit),
        buyIntervalSeconds: String(source.buyIntervalSeconds ?? base.buyIntervalSeconds),
        buyMeleeDefense: Boolean(source.buyMeleeDefense ?? base.buyMeleeDefense),
        minimumMeleeDefense: String(source.minimumMeleeDefense ?? base.minimumMeleeDefense),
        buyRangeDefense: Boolean(source.buyRangeDefense ?? base.buyRangeDefense),
        minimumRangeDefense: String(source.minimumRangeDefense ?? base.minimumRangeDefense),
        troopPackageTiers: normalizePackageTiers(source.troopPackageTiers),
        medalOverflowProtection: Boolean(source.medalOverflowProtection ?? base.medalOverflowProtection),
        minimumCoins: String(source.minimumCoins ?? base.minimumCoins),
        minimumKhanTablets: String(source.minimumKhanTablets ?? base.minimumKhanTablets),
        buyHurlingRocks: Boolean(source.buyHurlingRocks ?? base.buyHurlingRocks),
        hurlingRocksMinimum: String(source.hurlingRocksMinimum ?? base.hurlingRocksMinimum),
        buyCastleGateReinforcement: Boolean(source.buyCastleGateReinforcement ?? base.buyCastleGateReinforcement),
        castleGateReinforcementMinimum: String(source.castleGateReinforcementMinimum ?? base.castleGateReinforcementMinimum),
        buyFlamingArrows: Boolean(source.buyFlamingArrows ?? base.buyFlamingArrows),
        flamingArrowsMinimum: String(source.flamingArrowsMinimum ?? base.flamingArrowsMinimum),
        buySharpenedStakes: Boolean(source.buySharpenedStakes ?? base.buySharpenedStakes),
        sharpenedStakesMinimum: String(source.sharpenedStakesMinimum ?? base.sharpenedStakesMinimum),
        buyBodkinArrowheads: Boolean(source.buyBodkinArrowheads ?? base.buyBodkinArrowheads),
        bodkinArrowheadsMinimum: String(source.bodkinArrowheadsMinimum ?? base.bodkinArrowheadsMinimum),
        keepTool: String(source.keepTool ?? base.keepTool),
        keepSupportTool: String(source.keepSupportTool ?? base.keepSupportTool),
        eventDifficulty: String(source.eventDifficulty ?? base.eventDifficulty),
        nomadsScoreShutoff: String(source.nomadsScoreShutoff ?? base.nomadsScoreShutoff),
        hanTauntTargetCount: String(source.hanTauntTargetCount ?? base.hanTauntTargetCount)
    }
}

const filterToolOptions = (options, state) => {
    const query = String(state.inputValue ?? '').trim().toLowerCase()
    if (!query)
        return options

    return options.filter(option =>
        String(option?.value ?? '').toLowerCase().includes(query) ||
        String(option?.label ?? '').toLowerCase().includes(query) ||
        String(option?.searchText ?? '').toLowerCase().includes(query) ||
        String(option?.statLine ?? '').toLowerCase().includes(query))
}

const parseCatalogSlotTypes = option =>
    (Array.isArray(option?.slotTypes) ? option.slotTypes : [])
        .map(value => Number(value))
        .filter(Number.isFinite)

export const filterDefenseToolsBySlotType = (options, slotType) => {
    const normalizedSlotType = Number(slotType)
    const fallbackGroup =
        normalizedSlotType === 1 ? '/ Wall' :
            normalizedSlotType === 2 ? '/ Gate' :
                normalizedSlotType === 4 ? '/ Moat' :
                    ''

    return (Array.isArray(options) ? options : []).filter(option => {
        const slotTypes = parseCatalogSlotTypes(option)
        if (slotTypes.includes(normalizedSlotType))
            return true

        return fallbackGroup ? String(option?.group ?? '').includes(fallbackGroup) : false
    })
}

const rebalanceSplit = (currentSplit, changedKey, nextValue) => {
    const selectedValue = clampPercent(nextValue)
    const otherKeys = ['left', 'middle', 'right'].filter(key => key !== changedKey)
    const remaining = Math.max(0, 100 - selectedValue)
    const currentOtherTotal = otherKeys.reduce((sum, key) => sum + clampPercent(currentSplit[key]), 0)

    if (currentOtherTotal <= 0)
        return {
            [changedKey]: selectedValue,
            [otherKeys[0]]: Math.floor(remaining / 2),
            [otherKeys[1]]: remaining - Math.floor(remaining / 2)
        }

    let assigned = 0
    const nextSplit = { [changedKey]: selectedValue }
    otherKeys.forEach((key, index) => {
        if (index === otherKeys.length - 1) {
            nextSplit[key] = Math.max(0, remaining - assigned)
            return
        }

        const portion = Math.round((clampPercent(currentSplit[key]) / currentOtherTotal) * remaining)
        nextSplit[key] = portion
        assigned += portion
    })

    return nextSplit
}

function SectionHeader({ title, t }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.2 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#f5f8ff' }}>{t(title)}</Typography>
            <Box sx={{ flexGrow: 1, height: 1, backgroundColor: 'rgba(255,255,255,0.12)' }} />
        </Box>
    )
}

function InfoText({ children }) {
    return <Typography variant="body2" color="text.secondary" sx={{ mt: 0.7, lineHeight: 1.35 }}>{children}</Typography>
}

function NumericField({ label, value, onChange, helperText, t }) {
    return (
        <Box>
            <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>{t(label)}</Typography>
            <TextField
                fullWidth
                size="small"
                value={value}
                onChange={event => onChange(event.target.value)}
                sx={FIELD_INPUT_SX}
                inputProps={{ inputMode: 'numeric' }}
            />
            {helperText ? <InfoText>{t(helperText)}</InfoText> : null}
        </Box>
    )
}

function TierCheckboxField({ label, checked, onChange, t }) {
    return (
        <Button
            fullWidth
            variant="outlined"
            onClick={() => onChange(!checked)}
            sx={{
                justifyContent: 'flex-start',
                gap: 1.2,
                minHeight: 46,
                px: 1.4,
                borderRadius: '10px',
                borderColor: checked ? 'rgba(47, 124, 255, 0.58)' : 'rgba(112, 136, 174, 0.22)',
                backgroundColor: checked ? 'rgba(47, 124, 255, 0.16)' : 'rgba(23, 34, 55, 0.58)',
                color: '#eef4ff',
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': {
                    borderColor: checked ? 'rgba(47, 124, 255, 0.7)' : 'rgba(112, 136, 174, 0.38)',
                    backgroundColor: checked ? 'rgba(47, 124, 255, 0.2)' : 'rgba(27, 39, 63, 0.78)'
                }
            }}
        >
            <Checkbox
                checked={checked}
                tabIndex={-1}
                disableRipple
                sx={{
                    p: 0,
                    color: 'rgba(169, 181, 207, 0.72)',
                    '&.Mui-checked': {
                        color: '#dce8ff'
                    }
                }}
            />
            <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>{t(label)}</Typography>
        </Button>
    )
}

function ToggleField({ label, checked, onChange, helperText, t, iconUrl = '', iconType = 'resource' }) {
    return (
        <Box sx={{ minHeight: iconUrl ? 72 : helperText ? 56 : 36 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, pt: 0.25 }}>
                {iconUrl ? <CompactAssetIcon iconUrl={iconUrl} itemType={iconType} size={56} /> : null}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                        <Switch checked={Boolean(checked)} onChange={(_, value) => onChange(value)} sx={TOGGLE_SWITCH_SX} />
                        <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, minWidth: 0 }}>
                            {t(label)}
                        </Typography>
                    </Box>
                    {helperText ? <InfoText>{t(helperText)}</InfoText> : null}
                </Box>
            </Box>
        </Box>
    )
}

function ToolField({ label, value, options, onChange, helperText, t }) {
    const selectedOption = React.useMemo(
        () => options.find(option => String(option.value) === String(value)) ?? null,
        [options, value]
    )

    return (
        <Box>
            <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>{t(label)}</Typography>
            <Autocomplete
                fullWidth
                size="small"
                options={options}
                value={selectedOption}
                popupIcon={<SearchRoundedIcon />}
                filterOptions={filterToolOptions}
                groupBy={option => String(option.group ?? '').split(' / ')[1] ?? option.group}
                getOptionLabel={option => `${option.value}: ${option.label}`}
                isOptionEqualToValue={(option, nextValue) => option.value === nextValue.value}
                onChange={(_, nextOption) => onChange(nextOption?.value ?? '')}
                renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                        <UnitIconFrame iconUrl={option.iconUrl} itemType="tool" size={38} />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                {`${option.value}: ${option.label}`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {option.statLine}
                            </Typography>
                        </Box>
                    </Box>
                )}
                renderInput={params => (
                    <TextField
                        {...params}
                        placeholder={t('Search tool by name or ID')}
                        sx={FIELD_INPUT_SX}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    <InputAdornment position="end">
                                        <SearchRoundedIcon sx={{ color: 'rgba(255,255,255,0.55)' }} />
                                    </InputAdornment>
                                    {params.InputProps.endAdornment}
                                </>
                            )
                        }}
                    />
                )}
                noOptionsText={t('No matching tools')}
            />
            {selectedOption ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <UnitIconFrame iconUrl={selectedOption.iconUrl} itemType="tool" size={34} />
                    <Chip size="small" label={t('Tool')} sx={{ backgroundColor: 'rgba(108, 132, 170, 0.18)' }} />
                </Box>
            ) : (
                <Chip size="small" label={t('Tool')} sx={{ mt: 1, backgroundColor: 'rgba(108, 132, 170, 0.18)' }} />
            )}
            {helperText ? <InfoText>{t(helperText)}</InfoText> : null}
        </Box>
    )
}

function TemplateSearchField({ label, helperText, value, placeholder, ownerUsername, onOpen, onClear, t }) {
    return (
        <Box>
            <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>{t(label)}</Typography>
            <TextField
                fullWidth
                value={value}
                placeholder={t(placeholder)}
                onClick={onOpen}
                sx={FIELD_INPUT_SX}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={onOpen} edge="end" sx={{ color: '#d9e4fb' }}>
                                <SearchRoundedIcon />
                            </IconButton>
                        </InputAdornment>
                    )
                }}
            />
            {ownerUsername ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mt: 1 }}>
                    <PersonOutlineRoundedIcon sx={{ fontSize: 15, color: '#94a4c3' }} />
                    <Typography sx={{ color: '#94a4c3', fontSize: '0.9rem' }}>{`${t('Saved by')} ${ownerUsername}`}</Typography>
                </Box>
            ) : null}
            {onClear && value ? (
                <Button variant="outlined" onClick={onClear} sx={OUTLINED_ACTION_SX}>
                    {t('Clear selected template')}
                </Button>
            ) : null}
            {helperText ? <InfoText>{t(helperText)}</InfoText> : null}
        </Box>
    )
}

function TemplatePickerDialog({ open, templates, loading, onClose, onSelect, t }) {
    const [searchTerm, setSearchTerm] = React.useState('')

    React.useEffect(() => {
        if (open)
            setSearchTerm('')
    }, [open])

    const filteredTemplates = React.useMemo(() => {
        const normalizedQuery = String(searchTerm ?? '').trim().toLowerCase()
        if (!normalizedQuery)
            return templates

        return templates.filter(template =>
            `${template?.name ?? ''} ${template?.description ?? ''} ${template?.ownerUsername ?? ''}`
                .toLowerCase()
                .includes(normalizedQuery)
        )
    }, [searchTerm, templates])

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: 'min(calc(100vw - 40px), 720px)',
                    maxWidth: '720px',
                    borderRadius: '16px',
                    background: 'linear-gradient(180deg, rgba(31,44,70,0.98) 0%, rgba(24,36,59,0.98) 100%)',
                    border: '1px solid rgba(255,255,255,0.08)'
                }
            }}
        >
            <DialogTitle sx={{ px: 2.5, pt: 2.2, pb: 1.3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                    <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: '#f3f7ff' }}>
                        {t('Select Attack Template')}
                    </Typography>
                    <IconButton onClick={onClose} sx={{ color: '#a9b4ca' }}>
                        <CloseRoundedIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ px: 2.5, pb: 2.5 }}>
                <TextField
                    fullWidth
                    placeholder={t('Search by name or user')}
                    value={searchTerm}
                    onChange={event => setSearchTerm(event.target.value)}
                    sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                            minHeight: 44,
                            borderRadius: '10px',
                            backgroundColor: 'rgba(17, 26, 44, 0.92)'
                        }
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchRoundedIcon sx={{ color: '#a9b4ca' }} />
                            </InputAdornment>
                        )
                    }}
                />

                <Box sx={{ maxHeight: 'min(52vh, 520px)', overflowY: 'auto', pr: 0.5 }}>
                    {loading ? (
                        <Box sx={{ py: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary">{t('Loading saved attack templates...')}</Typography>
                        </Box>
                    ) : filteredTemplates.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.3 }}>
                            {filteredTemplates.map(template => (
                                <Box
                                    key={template.id}
                                    onClick={() => onSelect(template)}
                                    sx={{
                                        p: 1.7,
                                        borderRadius: '12px',
                                        border: '1px solid rgba(255,255,255,0.08)',
                                        backgroundColor: 'rgba(255,255,255,0.03)',
                                        cursor: 'pointer',
                                        transition: 'background-color 160ms ease, border-color 160ms ease',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255,255,255,0.05)',
                                            borderColor: 'rgba(94, 157, 255, 0.36)'
                                        }
                                    }}
                                >
                                    <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#f2f6ff' }}>
                                        {template?.name || 'Untitled'}
                                    </Typography>
                                    <Typography sx={{ mt: 0.65, color: '#a8b4cc', fontSize: '0.94rem' }}>
                                        {template?.description || t('No description')}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.65, mt: 1.1 }}>
                                        <PersonOutlineRoundedIcon sx={{ fontSize: 15, color: '#94a4c3' }} />
                                        <Typography sx={{ color: '#94a4c3', fontSize: '0.9rem' }}>
                                            {`${t('Saved by')} ${template?.ownerUsername || 'Unknown'}`}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                p: 2.2,
                                borderRadius: '12px',
                                border: '1px solid rgba(255,255,255,0.08)',
                                backgroundColor: 'rgba(255,255,255,0.03)'
                            }}
                        >
                            <Typography sx={{ fontWeight: 700, color: '#f2f6ff' }}>
                                {t('No saved attack templates yet.')}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    )
}

function DistributionSlider({ label, value, color, onChange, t }) {
    return (
        <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#c7d7f7' }}>{t(label).toUpperCase()}</Typography>
                    <LockOutlinedIcon sx={{ fontSize: 14, color: 'rgba(180, 194, 223, 0.5)' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#f5f8ff' }}>{value}%</Typography>
            </Box>
            <Slider
                min={0}
                max={100}
                value={value}
                onChange={(_, nextValue) => onChange(nextValue)}
                sx={{ color, '& .MuiSlider-thumb': { width: 18, height: 18 } }}
            />
        </Box>
    )
}

export default function KhanAttackConfigurator({ parentKey, userPlugins, onChange, appLanguage = 'en', hideWorkspaceHeader = false, templatePlatform = 'gge' }) {
    const pluginState = React.useMemo(() => userPlugins[parentKey] ?? {}, [parentKey, userPlugins])
    const config = React.useMemo(() => normalizeConfig(pluginState[KHAN_CONFIG_KEY]), [pluginState])
    const [catalogLoading, setCatalogLoading] = React.useState(true)
    const [catalogError, setCatalogError] = React.useState('')
    const [defenseTools, setDefenseTools] = React.useState([])
    const [templates, setTemplates] = React.useState([])
    const [templatesLoading, setTemplatesLoading] = React.useState(true)
    const [templatePickerOpen, setTemplatePickerOpen] = React.useState(false)
    const khanTemplateField = React.useMemo(() =>
        ATTACK_TEMPLATE_FIELDS.find(field => field.id === 'khan'),
    [])
    const selectedAttackTemplate = React.useMemo(
        () => getSavedAttackTemplateReference(pluginState, getAttackSetupAssignment(userPlugins, 'khan')),
        [pluginState, userPlugins]
    )
    const t = useAutoTranslate(appLanguage, [
        'Configuration Editor', "Edit your bot's configuration", 'Khan Bot Settings', 'Configure Khan camp farming parameters',
        'General Settings',
        'Attack Template', 'Khan Attack Template', 'Select a saved Khan attack template', 'Linked with Attack Setup. The selected template is saved once and used by Khan attacks.', 'Clear selected template', 'Saved by', 'Search by name or user', 'Select Attack Template', 'Loading saved attack templates...', 'No saved attack templates yet.', 'No description',
        'Attack Settings', 'Attack Count Threshold', 'Maximum global attack count before the bot pauses (shared counter with other attack modules)', 'Max Khan Attacks', 'Maximum number of attacks Khan bot should send before stopping (0 = unlimited)', 'Max Chain Length', 'Maximum chain length before stopping new attacks (0 = unlimited). Chain = consecutive attacks within 400 seconds', 'Refill Tools After Hits', 'Number of attacks received on your Castle before refilling moat, wall, and courtyard tools',
        'Skip Settings', 'Allowed Skip Types', 'Select All', 'Clear All', 'Select which skip types are allowed for auto-skipping cooldowns.',
        'Courtyard Settings', 'Use Khan Chests', 'Whether to use Khan chests for courtyard defense when refilling tools', 'Courtyard Melee Percentage', 'Percentage of melee troops in the courtyard (0-100)',
        'Moat Tools', 'Moat Left Tool', 'Tool to use for the left moat section during refill', 'Moat Middle Tool', 'Tool to use for the middle moat section during refill', 'Moat Right Tool', 'Tool to use for the right moat section during refill',
        'Wall Tools', 'Wall Left Tool', 'Tool for the left wall section', 'Wall Middle Tool', 'Tool for the middle wall section', 'Wall Right Tool', 'Tool for the right wall section', 'Wall Middle Gate Tool', 'Tool for the center gate slot',
        'Keep Tools', 'Keep Tool', 'Tool to fill the keep slots (traps, hoardings, cauldrons)',
        'Keep Support Tools', 'Keep Support Tool', 'Tool to fill the keep support slots (dummy soldiers, special items)',
        'Wall Troop Settings', 'Wall Melee Percentage', 'Percentage of melee troops to place on the wall vs ranged (0-100)', 'Wall Troop Distribution', 'Total', 'Left Flank', 'Middle', 'Right Flank', 'Troop distribution percentages across wall sections [left, middle, right]. Should sum to 100.',
        'Auto-Buy Troops', 'Buy Interval (seconds)', 'Seconds between buy checks', 'Buy Melee Defense', 'Auto-buy melee defense troops', 'Minimum Melee Defense', 'Buy when below this amount', 'Buy Range Defense', 'Auto-buy ranged defense troops', 'Minimum Range Defense',
        'Package Tiers', 'Troop Package Tiers', 'Enable All', 'Disable All', 'Medal Overflow Protection', 'Trade medals to tablets when reaching 2.1B to prevent int32 overflow', 'Bot buys Tier 1 first, then Tier 2, then Tier 3. Enabling a higher tier automatically enables lower tiers.', 'Enable/disable package tiers (95/475/1425 medals for 10 troops). Bot buys Tier 1 first, then Tier 2, then Tier 3. Max 20000 per package.',
        'Buy Defense Tools', 'Minimum Coins', 'Only buy tools when coin balance exceeds this amount', 'Minimum Khan Tablets', 'Only buy bodkin arrows when Khan tablet balance exceeds this amount', 'Hurling Rocks Minimum', 'Castle Gate Reinforcement Minimum', 'Flaming Arrows Minimum', 'Sharpened Stakes Minimum', 'Bodkin Arrowheads Minimum', 'Auto-buy Hurling Rocks when below minimum', 'Auto-buy Castle Gate Reinforcement when below minimum', 'Auto-buy Flaming Arrows when below minimum', 'Auto-buy Sharpened Stakes when below minimum', 'Auto-buy Bodkin Arrowheads when below minimum',
        'Tool', 'Search tool by name or ID', 'No matching tools', 'Defense tool catalog is unavailable right now.', 'Loading defense tool catalog...',
        'Event Difficulty', 'Khan event difficulty level (default: 3, range: 0-4)',
        'Score Shutoff', 'Stop attacking when event score reaches this value (0 = disabled)',
        'Han Taunt Target Count', 'Number of Han taunts to perform before stopping (0 = disabled)'
    ])

    React.useEffect(() => {
        let cancelled = false

        fetch(`/attack-template-catalog.json?language=${encodeURIComponent(appLanguage || 'en')}`, { credentials: 'same-origin' })
            .then(async response => {
                if (!response.ok)
                    throw new Error(`Catalog request failed with ${response.status}`)
                return response.json()
            })
            .then(payload => {
                if (cancelled)
                    return

                setDefenseTools(Array.isArray(payload?.defenseTools) ? payload.defenseTools : [])
                setCatalogError('')
            })
            .catch(error => {
                if (!cancelled)
                    setCatalogError(error?.message ?? 'Defense tool catalog is unavailable right now.')
            })
            .finally(() => {
                if (!cancelled)
                    setCatalogLoading(false)
            })

        return () => {
            cancelled = true
        }
    }, [appLanguage])

    React.useEffect(() => {
        let cancelled = false
        setTemplatesLoading(true)

        fetch(buildSavedAttackTemplatesUrl(templatePlatform), { credentials: 'same-origin' })
            .then(response => response.ok ? response.json() : { templates: [] })
            .then(payload => {
                if (cancelled)
                    return

                setTemplates(sortTemplatesByUpdatedAt(payload?.templates))
            })
            .catch(() => {
                if (!cancelled)
                    setTemplates([])
            })
            .finally(() => {
                if (!cancelled)
                    setTemplatesLoading(false)
            })

        return () => {
            cancelled = true
        }
    }, [templatePlatform])

    const moatTools = React.useMemo(() => filterDefenseToolsBySlotType(defenseTools, 4), [defenseTools])
    const wallTools = React.useMemo(() => filterDefenseToolsBySlotType(defenseTools, 1), [defenseTools])
    const gateTools = React.useMemo(() => filterDefenseToolsBySlotType(defenseTools, 2), [defenseTools])
    const keepTools = React.useMemo(() => filterDefenseToolsBySlotType(defenseTools, 6), [defenseTools])
    const keepSupportTools = React.useMemo(() => filterDefenseToolsBySlotType(defenseTools, 5), [defenseTools])

    const updatePluginState = React.useCallback((updater) => {
        const nextPlugins = cloneValue(userPlugins ?? {})
        nextPlugins[parentKey] ??= {}
        updater(nextPlugins[parentKey])
        nextPlugins[parentKey][KHAN_CONFIG_KEY] = normalizeConfig(nextPlugins[parentKey][KHAN_CONFIG_KEY])
        onChange(nextPlugins)
    }, [onChange, parentKey, userPlugins])

    const updateConfigField = (key, value) => updatePluginState(nextState => {
        const nextConfig = normalizeConfig(nextState[KHAN_CONFIG_KEY])
        nextConfig[key] = value
        nextState[KHAN_CONFIG_KEY] = nextConfig
    })

    const updateWallSplit = (key, value) => updatePluginState(nextState => {
        const nextConfig = normalizeConfig(nextState[KHAN_CONFIG_KEY])
        nextConfig.wallTroopSplit = rebalanceSplit(nextConfig.wallTroopSplit, key, value)
        nextState[KHAN_CONFIG_KEY] = nextConfig
    })

    const toggleSkipType = skipValue => updatePluginState(nextState => {
        const nextConfig = normalizeConfig(nextState[KHAN_CONFIG_KEY])
        const values = new Set(nextConfig.allowedSkipTypes)
        if (values.has(skipValue))
            values.delete(skipValue)
        else
            values.add(skipValue)
        nextConfig.allowedSkipTypes = Array.from(values).sort((left, right) => left - right)
        nextState[KHAN_CONFIG_KEY] = nextConfig
    })

    const setAllSkipTypes = enabled => updatePluginState(nextState => {
        const nextConfig = normalizeConfig(nextState[KHAN_CONFIG_KEY])
        nextConfig.allowedSkipTypes = enabled ? SKIP_OPTIONS.map(option => option.value) : []
        nextState[KHAN_CONFIG_KEY] = nextConfig
    })

    const setTierValue = (tierKey, troopKey, enabled) => updatePluginState(nextState => {
        const nextConfig = normalizeConfig(nextState[KHAN_CONFIG_KEY])
        const tiers = normalizePackageTiers(nextConfig.troopPackageTiers)
        const tierIndex = PACKAGE_TIERS.findIndex(tier => tier.key === tierKey)
        tiers[tierKey][troopKey] = enabled
        if (enabled && tierIndex > 0)
            for (let index = 0; index < tierIndex; index++)
                tiers[PACKAGE_TIERS[index].key][troopKey] = true
        nextConfig.troopPackageTiers = tiers
        nextState[KHAN_CONFIG_KEY] = nextConfig
    })

    const setAllTiers = enabled => updatePluginState(nextState => {
        const nextConfig = normalizeConfig(nextState[KHAN_CONFIG_KEY])
        nextConfig.troopPackageTiers = PACKAGE_TIERS.reduce((tiers, tier) => {
            tiers[tier.key] = { melee: enabled, ranged: enabled }
            return tiers
        }, {})
        nextState[KHAN_CONFIG_KEY] = nextConfig
    })

    const handleTemplateSelect = template => {
        const nextPlugins = cloneValue(userPlugins ?? {})
        syncAttackTemplateSelection(nextPlugins, 'khan', template)
        onChange(nextPlugins)
        setTemplatePickerOpen(false)
    }

    const handleTemplateClear = () => {
        const nextPlugins = cloneValue(userPlugins ?? {})
        syncAttackTemplateSelection(nextPlugins, 'khan', null)
        onChange(nextPlugins)
    }

    const meleeTierCount = PACKAGE_TIERS.filter(tier => config.troopPackageTiers[tier.key].melee).length
    const rangedTierCount = PACKAGE_TIERS.filter(tier => config.troopPackageTiers[tier.key].ranged).length

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {!hideWorkspaceHeader ? (
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#f5f8ff', mb: 0.5 }}>{t('Configuration Editor')}</Typography>
                    <Typography variant="body1" color="text.secondary">{t("Edit your bot's configuration")}</Typography>
                </Box>
            ) : null}

            <Card sx={PRIMARY_CARD_SX}>
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#f5f8ff', mb: 0.5 }}>{t('Khan Bot Settings')}</Typography>
                        <Typography variant="body1" color="text.secondary">{t('Configure Khan camp farming parameters')}</Typography>
                    </Box>

                    <SectionHeader title="Attack Template" t={t} />
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        <Grid item xs={12}>
                            <TemplateSearchField
                                label={khanTemplateField?.label ?? 'Khan Attack Template'}
                                helperText="Linked with Attack Setup. The selected template is saved once and used by Khan attacks."
                                value={selectedAttackTemplate.name}
                                placeholder={khanTemplateField?.placeholder ?? 'Select a saved Khan attack template'}
                                ownerUsername={selectedAttackTemplate.ownerUsername}
                                onOpen={() => setTemplatePickerOpen(true)}
                                onClear={selectedAttackTemplate.templateId ? handleTemplateClear : undefined}
                                t={t}
                            />
                        </Grid>
                    </Grid>

                    <SectionHeader title="Attack Settings" t={t} />
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={6}><NumericField label="Attack Count Threshold" value={config.attackCountThreshold} onChange={value => updateConfigField('attackCountThreshold', value)} helperText="Maximum global attack count before the bot pauses (shared counter with other attack modules)" t={t} /></Grid>
                        <Grid item xs={12} md={6}><NumericField label="Max Khan Attacks" value={config.maxKhanAttacks} onChange={value => updateConfigField('maxKhanAttacks', value)} helperText="Maximum number of attacks Khan bot should send before stopping (0 = unlimited)" t={t} /></Grid>
                        <Grid item xs={12} md={6}><NumericField label="Max Chain Length" value={config.maxChainLength} onChange={value => updateConfigField('maxChainLength', value)} helperText="Maximum chain length before stopping new attacks (0 = unlimited). Chain = consecutive attacks within 400 seconds" t={t} /></Grid>
                        <Grid item xs={12} md={6}><NumericField label="Refill Tools After Hits" value={config.refillToolsAfterHits} onChange={value => updateConfigField('refillToolsAfterHits', value)} helperText="Number of attacks received on your Castle before refilling moat, wall, and courtyard tools" t={t} /></Grid>
                        <Grid item xs={12} md={6}><NumericField label="Event Difficulty" value={config.eventDifficulty} onChange={value => updateConfigField('eventDifficulty', value)} helperText="Khan event difficulty level (default: 3, range: 0-4)" t={t} /></Grid>
                        <Grid item xs={12} md={6}><NumericField label="Score Shutoff" value={config.nomadsScoreShutoff} onChange={value => updateConfigField('nomadsScoreShutoff', value)} helperText="Stop attacking when event score reaches this value (0 = disabled)" t={t} /></Grid>
                        <Grid item xs={12} md={6}><NumericField label="Han Taunt Target Count" value={config.hanTauntTargetCount} onChange={value => updateConfigField('hanTauntTargetCount', value)} helperText="Number of Han taunts to perform before stopping (0 = disabled)" t={t} /></Grid>
                    </Grid>

                    <SectionHeader title="Skip Settings" t={t} />
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#f5f8ff', mb: 1.5 }}>{t('Allowed Skip Types')}</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.25 }}>
                            <Button variant="outlined" onClick={() => setAllSkipTypes(true)} sx={OUTLINED_ACTION_SX}>{t('Select All')}</Button>
                            <Button variant="outlined" color="inherit" onClick={() => setAllSkipTypes(false)} sx={OUTLINED_ACTION_SX}>{t('Clear All')}</Button>
                            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center', ml: 1 }}>{`${config.allowedSkipTypes.length} of ${SKIP_OPTIONS.length} selected`}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {SKIP_OPTIONS.map(option => {
                                const selected = config.allowedSkipTypes.includes(option.value)
                                return <Button key={option.value} variant={selected ? 'contained' : 'outlined'} onClick={() => toggleSkipType(option.value)} sx={{ ...OUTLINED_ACTION_SX, minWidth: 132, backgroundColor: selected ? 'rgba(47, 124, 255, 0.24)' : OUTLINED_ACTION_SX.backgroundColor, borderColor: selected ? 'rgba(47, 124, 255, 0.64)' : OUTLINED_ACTION_SX.borderColor }}>{option.label}</Button>
                            })}
                        </Box>
                        <InfoText>{t('Select which skip types are allowed for auto-skipping cooldowns.')}</InfoText>
                    </Box>

                    <SectionHeader title="Courtyard Settings" t={t} />
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={6}><ToggleField label="Use Khan Chests" checked={config.useKhanChests} onChange={value => updateConfigField('useKhanChests', value)} helperText="Whether to use Khan chests for courtyard defense when refilling tools" t={t} /></Grid>
                        <Grid item xs={12} md={6}><NumericField label="Courtyard Melee Percentage" value={config.courtyardMeleePercentage} onChange={value => updateConfigField('courtyardMeleePercentage', value)} helperText="Percentage of melee troops in the courtyard (0-100)" t={t} /></Grid>
                    </Grid>

                    {catalogError ? <Alert severity="warning" sx={{ mb: 3 }}>{catalogError}</Alert> : null}

                    <SectionHeader title="Moat Tools" t={t} />
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={6}><ToolField label="Moat Left Tool" value={config.moatLeftTool} options={moatTools} onChange={value => updateConfigField('moatLeftTool', value)} helperText="Tool to use for the left moat section during refill" t={t} /></Grid>
                        <Grid item xs={12} md={6}><ToolField label="Moat Middle Tool" value={config.moatMiddleTool} options={moatTools} onChange={value => updateConfigField('moatMiddleTool', value)} helperText="Tool to use for the middle moat section during refill" t={t} /></Grid>
                        <Grid item xs={12} md={6}><ToolField label="Moat Right Tool" value={config.moatRightTool} options={moatTools} onChange={value => updateConfigField('moatRightTool', value)} helperText="Tool to use for the right moat section during refill" t={t} /></Grid>
                    </Grid>

                    <SectionHeader title="Wall Tools" t={t} />
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={6}><ToolField label="Wall Left Tool" value={config.wallLeftTool} options={wallTools} onChange={value => updateConfigField('wallLeftTool', value)} helperText="Tool for the left wall section" t={t} /></Grid>
                        <Grid item xs={12} md={6}><ToolField label="Wall Middle Tool" value={config.wallMiddleTool} options={wallTools} onChange={value => updateConfigField('wallMiddleTool', value)} helperText="Tool for the middle wall section" t={t} /></Grid>
                        <Grid item xs={12} md={6}><ToolField label="Wall Right Tool" value={config.wallRightTool} options={wallTools} onChange={value => updateConfigField('wallRightTool', value)} helperText="Tool for the right wall section" t={t} /></Grid>
                        <Grid item xs={12} md={6}><ToolField label="Wall Middle Gate Tool" value={config.wallMiddleGateTool} options={gateTools} onChange={value => updateConfigField('wallMiddleGateTool', value)} helperText="Tool for the center gate slot" t={t} /></Grid>
                    </Grid>

                    <SectionHeader title="Keep Tools" t={t} />
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={6}><ToolField label="Keep Tool" value={config.keepTool} options={keepTools} onChange={value => updateConfigField('keepTool', value)} helperText="Tool to fill the keep slots (traps, hoardings, cauldrons)" t={t} /></Grid>
                        <Grid item xs={12} md={6}><ToolField label="Keep Support Tool" value={config.keepSupportTool} options={keepSupportTools} onChange={value => updateConfigField('keepSupportTool', value)} helperText="Tool to fill the keep support slots (dummy soldiers, special items)" t={t} /></Grid>
                    </Grid>

                    <SectionHeader title="Wall Troop Settings" t={t} />
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={6}><NumericField label="Wall Melee Percentage" value={config.wallMeleePercentage} onChange={value => updateConfigField('wallMeleePercentage', value)} helperText="Percentage of melee troops to place on the wall vs ranged (0-100)" t={t} /></Grid>
                        <Grid item xs={12} md={6}>
                            <Card sx={NESTED_CARD_SX}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#dce8ff' }}>{t('Wall Troop Distribution')}</Typography>
                                        <Chip label={`${t('Total')}: ${config.wallTroopSplit.left + config.wallTroopSplit.middle + config.wallTroopSplit.right}%`} sx={{ backgroundColor: 'rgba(24, 104, 72, 0.28)' }} />
                                    </Box>
                                    <DistributionSlider label="Left Flank" value={config.wallTroopSplit.left} color="#3b82f6" onChange={value => updateWallSplit('left', value)} t={t} />
                                    <DistributionSlider label="Middle" value={config.wallTroopSplit.middle} color="#10b981" onChange={value => updateWallSplit('middle', value)} t={t} />
                                    <DistributionSlider label="Right Flank" value={config.wallTroopSplit.right} color="#a855f7" onChange={value => updateWallSplit('right', value)} t={t} />
                                    <InfoText>{t('Troop distribution percentages across wall sections [left, middle, right]. Should sum to 100.')}</InfoText>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    <SectionHeader title="Auto-Buy Troops" t={t} />
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={6}><NumericField label="Buy Interval (seconds)" value={config.buyIntervalSeconds} onChange={value => updateConfigField('buyIntervalSeconds', value)} helperText="Seconds between buy checks" t={t} /></Grid>
                        <Grid item xs={12} md={6}><ToggleField label="Buy Melee Defense" checked={config.buyMeleeDefense} onChange={value => updateConfigField('buyMeleeDefense', value)} helperText="Auto-buy melee defense troops" t={t} /></Grid>
                        <Grid item xs={12} md={6}><NumericField label="Minimum Melee Defense" value={config.minimumMeleeDefense} onChange={value => updateConfigField('minimumMeleeDefense', value)} helperText="Buy when below this amount" t={t} /></Grid>
                        <Grid item xs={12} md={6}><ToggleField label="Buy Range Defense" checked={config.buyRangeDefense} onChange={value => updateConfigField('buyRangeDefense', value)} helperText="Auto-buy ranged defense troops" t={t} /></Grid>
                        <Grid item xs={12} md={6}><NumericField label="Minimum Range Defense" value={config.minimumRangeDefense} onChange={value => updateConfigField('minimumRangeDefense', value)} helperText="Buy when below this amount" t={t} /></Grid>
                    </Grid>

                    <SectionHeader title="Package Tiers" t={t} />
                    <Box sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#f5f8ff', mr: 1 }}>{t('Troop Package Tiers')}</Typography>
                            <Button variant="outlined" onClick={() => setAllTiers(true)} sx={OUTLINED_ACTION_SX}>{t('Enable All')}</Button>
                            <Button variant="outlined" color="inherit" onClick={() => setAllTiers(false)} sx={OUTLINED_ACTION_SX}>{t('Disable All')}</Button>
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>{`Melee: ${meleeTierCount}/3 | Ranged: ${rangedTierCount}/3`}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {PACKAGE_TIERS.map(tier => (
                                <Card key={tier.key} sx={NESTED_CARD_SX}>
                                    <CardContent>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#f5f8ff', mb: 1.6 }}>
                                            {tier.label} <Typography component="span" variant="body1" color="text.secondary">({tier.medals} medals)</Typography>
                                        </Typography>
                                        <Grid container spacing={1.5}>
                                            <Grid item xs={12} md={6}>
                                                <TierCheckboxField
                                                    label="Melee"
                                                    checked={config.troopPackageTiers[tier.key].melee}
                                                    onChange={enabled => setTierValue(tier.key, 'melee', enabled)}
                                                    t={t}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TierCheckboxField
                                                    label="Ranged"
                                                    checked={config.troopPackageTiers[tier.key].ranged}
                                                    onChange={enabled => setTierValue(tier.key, 'ranged', enabled)}
                                                    t={t}
                                                />
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                        <InfoText>{t('Bot buys Tier 1 first, then Tier 2, then Tier 3. Enabling a higher tier automatically enables lower tiers.')}</InfoText>
                        <InfoText>{t('Enable/disable package tiers (95/475/1425 medals for 10 troops). Bot buys Tier 1 first, then Tier 2, then Tier 3. Max 20000 per package.')}</InfoText>
                        <Box sx={{ mt: 2 }}>
                            <ToggleField label="Medal Overflow Protection" checked={config.medalOverflowProtection} onChange={value => updateConfigField('medalOverflowProtection', value)} helperText="Trade medals to tablets when reaching 2.1B to prevent int32 overflow" t={t} />
                        </Box>
                    </Box>

                    <SectionHeader title="Buy Defense Tools" t={t} />
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}><NumericField label="Minimum Coins" value={config.minimumCoins} onChange={value => updateConfigField('minimumCoins', value)} helperText="Only buy tools when coin balance exceeds this amount" t={t} /></Grid>
                        <Grid item xs={12} md={6}><NumericField label="Minimum Khan Tablets" value={config.minimumKhanTablets} onChange={value => updateConfigField('minimumKhanTablets', value)} helperText="Only buy bodkin arrows when Khan tablet balance exceeds this amount" t={t} /></Grid>
                        {DEFENSE_TOOL_PURCHASES.map(([key, label, minimumKey, minimumLabel, helperText]) => (
                            <React.Fragment key={key}>
                                <Grid item xs={12} md={6}><ToggleField label={label} checked={config[key]} onChange={value => updateConfigField(key, value)} helperText={helperText} t={t} iconUrl={KHAN_DEFENSE_TOOL_ICON_BY_KEY[key]} iconType="tool" /></Grid>
                                <Grid item xs={12} md={6}><NumericField label={minimumLabel} value={config[minimumKey]} onChange={value => updateConfigField(minimumKey, value)} helperText="Buy when below this amount" t={t} /></Grid>
                            </React.Fragment>
                        ))}
                    </Grid>

                    {catalogLoading ? <Alert severity="info" sx={{ mt: 3 }}>{t('Loading defense tool catalog...')}</Alert> : null}
                </CardContent>
            </Card>

            <TemplatePickerDialog
                open={templatePickerOpen}
                templates={templates}
                loading={templatesLoading}
                onClose={() => setTemplatePickerOpen(false)}
                onSelect={handleTemplateSelect}
                t={t}
            />
        </Box>
    )
}
